import { apiClient } from '@/lib/api-client';
import {
  Corporate,
  CorporateAdmin,
  CorporateEnrollment,
  CorporateEnrollmentListResponse,
  CreateCorporateRequest,
  UpdateCorporateRequest,
  AddCorporateAdminRequest,
  CreateCorporateEnrollmentRequest,
  InviteCorporateEnrollmentRequest,
  CorporateListResponse,
  CorporateAdminListResponse,
  ProgramTemplate,
  BulkInviteRequest,
  BulkInviteResult,
  BulkInviteValidationResult,
  ExecutiveSummary,
  PMDashboard,
  CorporateAvailableProgram,
  CorporateAvailableProgramListResponse,
  CreateCorporateAvailableProgramRequest,
  UpdateCorporateAvailableProgramRequest,
  CorporateAvailableAddon,
  CorporateAvailableAddonListResponse,
  CreateCorporateAvailableAddonRequest,
  UpdateCorporateAvailableAddonRequest,
  AssignCorporateProgramRequest,
  UpdateCorporateEnrollmentRequest,
} from '@/types/corporate';
import { isValidUUID } from '@/utils/validation';

const corporateApiUrl = 'users/corporate-accounts';
const corporateAdminApiUrl = 'users/corporate-admins';
const corporateEnrollmentApiUrl = 'users/corporate-enrollments';
const programTemplateApiUrl = 'users/program-templates';
const corporateAvailableProgramsApiUrl = 'users/corporate-available-programs';
const corporateAvailableAddonsApiUrl = 'users/corporate-available-addons';

// Corporate Accounts

export async function getCorporates(
  accessToken: string,
  params?: { status?: string; search?: string }
): Promise<CorporateListResponse | Corporate[]> {
  const response = await apiClient.get(corporateApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params,
  });
  return response.data;
}

export async function getCorporate(corporateId: string, accessToken: string): Promise<Corporate> {
  const response = await apiClient.get(`${corporateApiUrl}/${corporateId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function createCorporate(
  data: CreateCorporateRequest,
  accessToken: string
): Promise<Corporate> {
  const response = await apiClient.post(corporateApiUrl, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function updateCorporate(
  corporateId: string,
  data: UpdateCorporateRequest,
  accessToken: string
): Promise<Corporate> {
  const response = await apiClient.patch(`${corporateApiUrl}/${corporateId}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function deleteCorporate(corporateId: string, accessToken: string): Promise<void> {
  await apiClient.delete(`${corporateApiUrl}/${corporateId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function activateCorporate(
  corporateId: string,
  accessToken: string
): Promise<Corporate> {
  const response = await apiClient.post(
    `${corporateApiUrl}/${corporateId}/activate`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

export async function suspendCorporate(
  corporateId: string,
  accessToken: string
): Promise<Corporate> {
  const response = await apiClient.post(
    `${corporateApiUrl}/${corporateId}/suspend`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

// Corporate Admins

export async function getCorporateAdmins(
  corporateId: string,
  accessToken: string,
  params?: { admin_role?: string; is_active?: boolean }
): Promise<CorporateAdminListResponse> {
  const response = await apiClient.get(corporateAdminApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { corporate_id: corporateId, ...params },
  });
  return response.data;
}

export async function getCorporateAdmin(
  adminId: string,
  accessToken: string
): Promise<CorporateAdmin> {
  const response = await apiClient.get(`${corporateAdminApiUrl}/${adminId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function addCorporateAdmin(
  data: AddCorporateAdminRequest,
  accessToken: string
): Promise<CorporateAdmin> {
  const response = await apiClient.post(corporateAdminApiUrl, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function updateCorporateAdmin(
  adminId: string,
  data: { admin_role?: string; is_active?: boolean },
  accessToken: string
): Promise<CorporateAdmin> {
  const response = await apiClient.patch(`${corporateAdminApiUrl}/${adminId}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function removeCorporateAdmin(adminId: string, accessToken: string): Promise<void> {
  await apiClient.delete(`${corporateAdminApiUrl}/${adminId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

// Program Templates

export async function getProgramTemplates(
  accessToken: string,
  params?: { focus_area?: string; search?: string }
): Promise<{ count: number; results: ProgramTemplate[] }> {
  const response = await apiClient.get(programTemplateApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params,
  });
  return response.data;
}

export async function getProgramTemplate(
  templateId: string,
  accessToken: string
): Promise<ProgramTemplate> {
  const response = await apiClient.get(`${programTemplateApiUrl}/${templateId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

// Corporate Enrollments (Employees)

export async function getCorporateEnrollments(
  corporateId: string,
  accessToken: string,
  params?: { status?: string; search?: string; is_stalled?: boolean }
): Promise<CorporateEnrollmentListResponse> {
  const response = await apiClient.get(corporateEnrollmentApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { corporate: corporateId, ...params },
  });
  return response.data;
}

export async function getCorporateEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.get(`${corporateEnrollmentApiUrl}/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function createCorporateEnrollment(
  data: CreateCorporateEnrollmentRequest,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.post(corporateEnrollmentApiUrl, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function deleteCorporateEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<void> {
  await apiClient.delete(`${corporateEnrollmentApiUrl}/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function inviteCorporateEnrollment(
  data: InviteCorporateEnrollmentRequest,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.post(`${corporateEnrollmentApiUrl}/invite`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

// Bulk Invite Functions

const assertValidCorporateId = (corporateId: string): void => {
  if (!isValidUUID(corporateId)) {
    throw new Error('Invalid corporate ID format');
  }
};

const assertValidProgramId = (programId: string): void => {
  if (!isValidUUID(programId)) {
    throw new Error('Invalid program ID format');
  }
};

export async function downloadBulkInviteTemplate(accessToken: string): Promise<Blob> {
  const response = await apiClient.get(`${corporateEnrollmentApiUrl}/bulk-template`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    responseType: 'blob',
  });
  return response.data;
}

export async function validateBulkInvite(
  file: File,
  corporateId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<BulkInviteValidationResult> {
  assertValidCorporateId(corporateId);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('corporate', corporateId);

  const response = await apiClient.post<BulkInviteValidationResult>(
    `${corporateEnrollmentApiUrl}/bulk-validate`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data',
      },
      signal,
    }
  );
  return response.data;
}

/**
 * Bulk invite employees from an uploaded spreadsheet.
 * @param sendEmail - Sent as the string 'true' or 'false' for multipart form data
 * @param programId - Optional program UUID assigned to every invited employee
 * @param signal - Optional abort signal to cancel an in-flight request
 */
export async function bulkInviteEmployees(
  file: File,
  corporateId: string,
  accessToken: string,
  sendEmail: boolean = true,
  programId?: string,
  signal?: AbortSignal
): Promise<BulkInviteResult> {
  assertValidCorporateId(corporateId);
  if (programId) {
    assertValidProgramId(programId);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('corporate', corporateId);
  formData.append('send_email', String(sendEmail));
  if (programId) {
    formData.append('program', programId);
  }

  const response = await apiClient.post<BulkInviteResult>(
    `${corporateEnrollmentApiUrl}/bulk-invite`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data',
      },
      signal,
    }
  );
  return response.data;
}

export async function resendInvitation(
  enrollmentId: string,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.post(
    `${corporateEnrollmentApiUrl}/${enrollmentId}/resend-invitation`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

export async function assignCorporateProgram(
  enrollmentId: string,
  data: AssignCorporateProgramRequest,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.post(
    `${corporateEnrollmentApiUrl}/${enrollmentId}/assign-program`,
    data,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

export async function updateCorporateEnrollment(
  enrollmentId: string,
  data: UpdateCorporateEnrollmentRequest,
  accessToken: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.patch(`${corporateEnrollmentApiUrl}/${enrollmentId}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

// Executive Summary

export async function getExecutiveSummary(
  corporateId: string,
  accessToken: string
): Promise<ExecutiveSummary> {
  const response = await apiClient.get(`${corporateApiUrl}/${corporateId}/executive-summary`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function downloadExecutiveSummaryPDF(
  corporateId: string,
  accessToken: string
): Promise<Blob> {
  const response = await apiClient.get(`${corporateApiUrl}/${corporateId}/executive-summary/pdf`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    responseType: 'blob',
  });
  return response.data;
}

// PM Dashboard (Superclinic Staff)

export async function getPMDashboard(accessToken: string): Promise<PMDashboard> {
  const response = await apiClient.get(`${corporateApiUrl}/pm-dashboard`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

// Corporate Available Programs

export async function getCorporateAvailablePrograms(
  corporateId: string,
  accessToken: string,
  params?: { is_active?: boolean }
): Promise<CorporateAvailableProgramListResponse> {
  const response = await apiClient.get(corporateAvailableProgramsApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { corporate_id: corporateId, ...params },
  });
  return response.data;
}

export async function getCorporateAvailableProgram(
  id: string,
  accessToken: string
): Promise<CorporateAvailableProgram> {
  const response = await apiClient.get(`${corporateAvailableProgramsApiUrl}/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function addCorporateAvailableProgram(
  data: CreateCorporateAvailableProgramRequest,
  accessToken: string
): Promise<CorporateAvailableProgram> {
  const response = await apiClient.post(corporateAvailableProgramsApiUrl, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function updateCorporateAvailableProgram(
  id: string,
  data: UpdateCorporateAvailableProgramRequest,
  accessToken: string
): Promise<CorporateAvailableProgram> {
  const response = await apiClient.patch(`${corporateAvailableProgramsApiUrl}/${id}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function removeCorporateAvailableProgram(
  id: string,
  accessToken: string
): Promise<void> {
  await apiClient.delete(`${corporateAvailableProgramsApiUrl}/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function setDefaultCorporateProgram(
  corporateId: string,
  programId: string,
  accessToken: string
): Promise<CorporateAvailableProgram> {
  const response = await apiClient.post(
    `${corporateAvailableProgramsApiUrl}/set-default`,
    { corporate_id: corporateId, program_id: programId },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

export async function reorderCorporatePrograms(
  corporateId: string,
  order: string[],
  accessToken: string
): Promise<CorporateAvailableProgram[]> {
  const response = await apiClient.post(
    `${corporateAvailableProgramsApiUrl}/reorder`,
    { corporate_id: corporateId, order },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

// Add Existing User to Corporate
export async function addExistingUserToCorporate(
  corporateId: string,
  userId: string,
  accessToken: string,
  programId?: string
): Promise<CorporateEnrollment> {
  const response = await apiClient.post(
    `${corporateEnrollmentApiUrl}/add-existing-user`,
    {
      corporate_id: corporateId,
      user_id: userId,
      program_id: programId,
    },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

// Corporate Available Addons

export async function getCorporateAvailableAddons(
  corporateId: string,
  accessToken: string,
  params?: { is_active?: boolean }
): Promise<CorporateAvailableAddonListResponse> {
  const response = await apiClient.get(corporateAvailableAddonsApiUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { corporate_id: corporateId, ...params },
  });
  return response.data;
}

export async function getCorporateAvailableAddon(
  id: string,
  accessToken: string
): Promise<CorporateAvailableAddon> {
  const response = await apiClient.get(`${corporateAvailableAddonsApiUrl}/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function addCorporateAvailableAddon(
  data: CreateCorporateAvailableAddonRequest,
  accessToken: string
): Promise<CorporateAvailableAddon> {
  const response = await apiClient.post(corporateAvailableAddonsApiUrl, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function updateCorporateAvailableAddon(
  id: string,
  data: UpdateCorporateAvailableAddonRequest,
  accessToken: string
): Promise<CorporateAvailableAddon> {
  const response = await apiClient.patch(`${corporateAvailableAddonsApiUrl}/${id}`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
}

export async function removeCorporateAvailableAddon(
  id: string,
  accessToken: string
): Promise<void> {
  await apiClient.delete(`${corporateAvailableAddonsApiUrl}/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function bulkAssignCorporateAddons(
  corporateId: string,
  addonIds: string[],
  accessToken: string
): Promise<CorporateAvailableAddon[]> {
  const response = await apiClient.post(
    `${corporateAvailableAddonsApiUrl}/bulk-assign`,
    { corporate_id: corporateId, addon_ids: addonIds },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
}

export async function removeAllCorporateAddons(
  corporateId: string,
  accessToken: string
): Promise<void> {
  await apiClient.delete(`${corporateAvailableAddonsApiUrl}/remove-all`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { corporate_id: corporateId },
  });
}
